import { useMemo } from 'react';

import { useShellTheme } from '@/shared/shell/ShellPreferencesContext';

import tokens from './tokens.json';

type Theme = 'light' | 'dark';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Il valore di un token (`bg.brand`, `chart.1`) nel tema dato. */
function tokenValue(path: string, theme: Theme): string {
  let node: unknown = tokens.color;
  for (const key of path.split('.')) node = isRecord(node) ? node[key] : undefined;
  const value = isRecord(node) ? node[theme] : undefined;
  if (typeof value !== 'string') {
    throw new Error(`Token di colore sconosciuto: ${path}`);
  }
  return value;
}

function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Riferimento CSS a un token (`rgb(var(--color-bg-brand))`): segue il tema da solo,
 * per HTML e CSS generati fuori da React (icone e popup di Leaflet).
 */
export function cssColor(path: string, alpha?: number): string {
  tokenValue(path, 'light');
  const name = path.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`).replaceAll('.', '-');
  return alpha === undefined ? `rgb(var(--color-${name}))` : `rgb(var(--color-${name}) / ${alpha})`;
}

/**
 * Colori dei token per chi non può usare le classi Tailwind (canvas, SVG, Leaflet).
 * `color('chart.1')` → hex del tema corrente; `color('bg.brand', 0.12)` → rgba.
 * Legge il tema dal contesto e non dalle variabili CSS: la classe `.dark` cambia
 * in un effetto, dopo che i figli hanno già renderizzato.
 */
export function useThemeColors() {
  const theme = useShellTheme();

  return useMemo(
    () => ({
      theme,
      color: (path: string, alpha?: number): string => {
        const hex = tokenValue(path, theme);
        return alpha === undefined ? hex : withAlpha(hex, alpha);
      },
    }),
    [theme]
  );
}
